import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, CardBody, Alert } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { fetchReceipts, fetchSuppliers, fetchCatalogue } from "../utils/apiService";
import { useOrg } from "../utils/orgContext";

const StatCard = ({ label, value, sub, accent, link, linkLabel }) => (
  <Card style={{ border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
    <CardBody style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.4rem' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: accent || '#1a1d23', lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: '0.35rem' }}>
          {sub}
        </div>
      )}
      {link && (
        <Link to={link} style={{ fontSize: '0.78rem', color: 'var(--color-primary)', textDecoration: 'none', display: 'inline-block', marginTop: '0.75rem' }}>
          {linkLabel || 'View all →'}
        </Link>
      )}
    </CardBody>
  </Card>
);

const PIE_COLORS = ['#5b8dee', '#4dab6e', '#e8706a', '#9b7fe8', '#e8a84d', '#4db8c8'];

const groupBySupplier = (receipts) => {
  const counts = {};
  receipts.forEach(r => {
    const key = r.description || 'Unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value }));
};

const groupByMonth = (receipts) => {
  const counts = {};
  receipts.forEach(r => {
    const d = r.createdOn_UTC ? new Date(r.createdOn_UTC) : null;
    if (!d || isNaN(d)) return;
    const sortKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[sortKey] = (counts[sortKey] || 0) + 1;
  });
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Fill in all months from Jan to current month, even if no receipts
  const allMonths = [];
  for (let m = 1; m <= currentMonth; m++) {
    const key = `${currentYear}-${String(m).padStart(2, '0')}`;
    allMonths.push([key, counts[key] || 0]);
  }

  return allMonths.map(([key, count]) => {
      const [year, month] = key.split('-');
      const label = new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'short', year: '2-digit' });
      return { month: label, count };
    });
};

const Home = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgId, ready } = useOrg();
  const [receipts, setReceipts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);

  useEffect(() => {
    if (!ready) return;
    const load = async () => {
      setLoading(true);
      try {
        let token = null;
        try { token = await getAccessTokenSilently(); } catch (e) {}

        const [receiptsResult, suppliersResult, catalogueResult] = await Promise.allSettled([
          fetchReceipts(token, orgId),
          fetchSuppliers(token, orgId),
          fetchCatalogue(token),
        ]);
        if (catalogueResult.status === 'fulfilled') {
          setComingSoon((catalogueResult.value?.comingSoon || []).slice(0, 8));
        }

        const failed = [];
        if (receiptsResult.status === 'fulfilled') {
          setReceipts(receiptsResult.value?.values || []);
        } else {
          failed.push('receipts');
        }
        if (suppliersResult.status === 'fulfilled') {
          const s = suppliersResult.value;
          setSuppliers(s?.values || s?.Params || (Array.isArray(s) ? s : []));
        } else {
          failed.push('suppliers');
        }
        setErrors(failed);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [getAccessTokenSilently, ready, orgId]);

  const connectedCount = suppliers.filter(s => (s.ActivationState || s.activationState) === 2).length;
  const latestReceipt = receipts.length > 0
    ? receipts.reduce((a, b) => new Date(a.createdOn_UTC) > new Date(b.createdOn_UTC) ? a : b, receipts[0])
    : null;
  const chartData = groupByMonth(receipts);
  const pieData = groupBySupplier(receipts);

  return (
    <div style={{ paddingBottom: '3rem' }}>
      <div className="mb-4">
        <h2 className="page-title mb-1">Dashboard</h2>
        <p className="page-subtitle">Your receipts and supplier activity at a glance</p>
      </div>

      {errors.length > 0 && (
        <Alert color="danger" className="mb-4">
          Could not load {errors.join(' and ')}. The figures below may be incomplete. Please try again in a moment.
        </Alert>
      )}

      <Row className="mb-4 g-3">
        <Col xs={12} sm={4}>
          <StatCard
            label="Total Receipts"
            value={loading ? '—' : receipts.length}
            sub={receipts.length > 0 ? 'across all suppliers' : 'no receipts yet'}
          />
        </Col>
        <Col xs={12} sm={4}>
          <StatCard
            label="Connected Suppliers"
            value={loading ? '—' : connectedCount}
            sub={connectedCount > 0
              ? `of ${suppliers.length} total`
              : <Link to="/suppliers/1">Connect one →</Link>}
            accent={connectedCount > 0 ? 'var(--color-success-mid)' : undefined}
            link="/suppliers/1"
            linkLabel="View all suppliers →"
          />
        </Col>
        <Col xs={12} sm={4}>
          <Card style={{ border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardBody style={{ padding: '1.25rem 1.5rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>
                Last Receipt
              </div>
              {loading ? (
                <div style={{ color: '#adb5bd', fontSize: '0.875rem' }}>Loading...</div>
              ) : latestReceipt ? (
                <>
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1, marginBottom: '0.6rem' }}>
                    {new Date(latestReceipt.createdOn_UTC).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {latestReceipt.description && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#adb5bd', width: '52px' }}>Source</span>
                        <span style={{ fontSize: '0.82rem', color: '#343a40', fontWeight: 500 }}>{latestReceipt.description}</span>
                      </div>
                    )}
                    {latestReceipt.fileName && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#adb5bd', width: '52px' }}>File</span>
                        <span style={{ fontSize: '0.82rem', color: '#343a40', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{latestReceipt.fileName}</span>
                      </div>
                    )}
                    {latestReceipt.id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#adb5bd', width: '52px' }}>ID</span>
                        <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>#{latestReceipt.id}</span>
                      </div>
                    )}
                  </div>
                  <Link to="/receipts" style={{ fontSize: '0.78rem', color: 'var(--color-primary)', textDecoration: 'none', display: 'inline-block', marginTop: '0.75rem' }}>
                    View all receipts →
                  </Link>
                </>
              ) : (
                <div style={{ fontSize: '0.875rem', color: '#adb5bd' }}>No receipts yet</div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4 g-3">
        <Col xs={12} lg={7}>
          <Card style={{ border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardBody style={{ padding: '1.5rem' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23', marginBottom: '1.25rem' }}>
                Receipts by Month
              </div>
              {loading ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>Loading...</div>
              ) : receipts.length === 0 ? (
                <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#adb5bd' }}>
                  <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>No receipt data yet</div>
                  <Link to="/suppliers/1" style={{ fontSize: '0.82rem' }}>Connect a supplier to get started →</Link>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} barSize={28} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6c757d' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#6c757d' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e9ecef', fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} cursor={{ fill: '#f7f8fa' }} />
                    <Bar dataKey="count" name="Receipts" fill="var(--chart-1)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xs={12} lg={5}>
          <Card style={{ border: '1px solid #e9ecef', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardBody style={{ padding: '1.5rem' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1d23', marginBottom: '1.25rem' }}>
                Receipts by Supplier
              </div>
              {loading ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#adb5bd', fontSize: '0.875rem' }}>Loading...</div>
              ) : pieData.length === 0 ? (
                <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#adb5bd' }}>
                  <div style={{ fontSize: '0.875rem' }}>No data yet</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="42%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e9ecef', fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.78rem' }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {/* Coming Soon Integrations */}
      {comingSoon.length > 0 && (
      <div className="mt-2">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1d23' }}>Coming Soon</div>
          <div style={{ height: '1px', flex: 1, background: '#e9ecef' }} />
          <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd' }}>Integrations</span>
        </div>
        <Row className="g-2">
          {comingSoon.map(({ name: title, iconUrl: icon }) => (
            <Col key={title} xs={6} sm={4} md={3}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', background: '#f8f9fa', borderRadius: '10px', padding: '0.65rem 0.85rem', border: '1px solid #e9ecef', opacity: 0.7, position: 'relative' }}>
                <img src={icon} alt={title} style={{ width: '26px', height: '26px', objectFit: 'contain', flexShrink: 0, filter: 'grayscale(50%)' }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#495057', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
                </div>
                <span style={{ position: 'absolute', top: '6px', right: '7px', fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: '#e9ecef', color: '#adb5bd', borderRadius: '3px', padding: '1px 4px' }}>
                  Soon
                </span>
              </div>
            </Col>
          ))}
        </Row>
      </div>
      )}
    </div>
  );
};

export default Home;
